// <guest-selector> Web Component
// Handles adults/children/rooms counter with dropdown.
// Usage: <guest-selector adults="2" children="0" rooms="1"
//          label-adults="Adults" label-children="Children"
//          label-rooms="Rooms" label-done="Done"></guest-selector>

class GuestSelector extends HTMLElement {
    connectedCallback() {
        this._render();
        this._wire();
    }

    get adults() { return parseInt(this.getAttribute('adults')) || 2; }
    set adults(v) { this.setAttribute('adults', v); }

    get children() { return parseInt(this.getAttribute('children')) || 0; }
    set children(v) { this.setAttribute('children', v); }

    get rooms() { return parseInt(this.getAttribute('rooms')) || 1; }
    set rooms(v) { this.setAttribute('rooms', v); }

    _render() {
        const adults = this.adults;
        const children = this.children;
        const rooms = this.rooms;
        const labelAdults = this.getAttribute('label-adults') || 'Adults';
        const labelChildren = this.getAttribute('label-children') || 'Children';
        const labelRooms = this.getAttribute('label-rooms') || 'Rooms';
        const labelDone = this.getAttribute('label-done') || 'Done';
        const displayText = this._formatDisplay(adults, children, rooms);

        this.innerHTML = `
            <div class="gs-root bg-white rounded flex items-center px-3 py-2.5 relative min-w-[180px] cursor-pointer">
                <svg class="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                <button type="button" class="gs-trigger flex-1 text-sm font-medium text-left whitespace-nowrap text-gray-700 flex items-center">
                    <span class="gs-display">${displayText}</span>
                    <svg class="w-4 h-4 ml-1 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
                </button>
                <input type="hidden" name="adults" value="${adults}" class="gs-adults">
                <input type="hidden" name="children" value="${children}" class="gs-children">
                <input type="hidden" name="rooms" value="${rooms}" class="gs-rooms">

                <div class="gs-dropdown hidden absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border p-4 min-w-[280px] z-50 text-gray-700">
                    <div class="space-y-4">
                        ${this._counterRow('adults', labelAdults, adults)}
                        ${this._counterRow('children', labelChildren, children)}
                        ${this._counterRow('rooms', labelRooms, rooms)}
                    </div>
                    <button type="button" class="gs-done mt-4 w-full py-2 bg-booking-blue text-white rounded text-sm hover:bg-booking-blue-dark">
                        ${labelDone}
                    </button>
                </div>
            </div>`;
    }

    _counterRow(type, label, value) {
        return `
            <div class="flex items-center justify-between">
                <label class="text-sm">${label}</label>
                <div class="flex items-center gap-3">
                    <button type="button" class="gs-btn w-8 h-8 rounded-full border text-booking-blue hover:bg-gray-100" data-type="${type}" data-delta="-1">-</button>
                    <span class="gs-val w-8 text-center" data-type="${type}">${value}</span>
                    <button type="button" class="gs-btn w-8 h-8 rounded-full border text-booking-blue hover:bg-gray-100" data-type="${type}" data-delta="1">+</button>
                </div>
            </div>`;
    }

    _wire() {
        const root = this.querySelector('.gs-root');
        const dropdown = this.querySelector('.gs-dropdown');
        const done = this.querySelector('.gs-done');

        // Clicking anywhere on the root (icon, text, chevron) toggles the dropdown on desktop,
        // or opens the full-screen overlay on mobile.
        root.addEventListener('click', (e) => {
            if (dropdown.contains(e.target)) return;
            e.stopPropagation();
            if (this._isMobile()) {
                this._openOverlay();
                return;
            }
            dropdown.classList.toggle('hidden');
        });

        done.addEventListener('click', () => dropdown.classList.add('hidden'));

        document.addEventListener('click', (e) => {
            if (!this.contains(e.target)) dropdown.classList.add('hidden');
        });

        // Counter buttons (desktop dropdown)
        this.querySelectorAll('.gs-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const type = btn.dataset.type;
                const delta = parseInt(btn.dataset.delta);
                this._updateCount(type, delta);
            });
        });
    }

    _isMobile() {
        return window.matchMedia('(max-width: 767px)').matches;
    }

    _openOverlay() {
        if (this._overlay) return;

        const labelAdults = this.getAttribute('label-adults') || 'Adults';
        const labelChildren = this.getAttribute('label-children') || 'Children';
        const labelRooms = this.getAttribute('label-rooms') || 'Rooms';
        const labelDone = this.getAttribute('label-done') || 'Done';

        const adults = parseInt(this.querySelector('.gs-adults').value);
        const children = parseInt(this.querySelector('.gs-children').value);
        const rooms = parseInt(this.querySelector('.gs-rooms').value);

        const overlay = document.createElement('div');
        overlay.className = 'gs-overlay fixed inset-0 z-[100] bg-white flex flex-col';
        overlay.innerHTML = `
            <div class="flex items-center gap-2 px-2 py-2 border-b border-gray-200 bg-white">
                <button type="button" class="gs-ov-close p-2 text-gray-700 flex-shrink-0" aria-label="Close">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                    </svg>
                </button>
                <h2 class="flex-1 text-base font-semibold text-gray-900">Guests &amp; rooms</h2>
            </div>
            <div class="flex-1 overflow-y-auto px-5 py-4 divide-y divide-gray-100 text-gray-700">
                ${this._ovRow('adults', labelAdults, adults)}
                ${this._ovRow('children', labelChildren, children)}
                ${this._ovRow('rooms', labelRooms, rooms)}
            </div>
            <div class="px-4 py-3 border-t border-gray-200 bg-white">
                <button type="button" class="gs-ov-done w-full py-3 bg-booking-action text-white font-semibold rounded hover:bg-booking-action-dark">${labelDone}</button>
            </div>
        `;
        document.body.appendChild(overlay);
        document.body.style.overflow = 'hidden';
        this._overlay = overlay;

        overlay.querySelectorAll('.gs-ov-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const type = btn.dataset.type;
                const delta = parseInt(btn.dataset.delta);
                this._updateCount(type, delta);
                const valEl = overlay.querySelector(`.gs-ov-val[data-type="${type}"]`);
                if (valEl) valEl.textContent = this.querySelector(`.gs-${type}`).value;
            });
        });

        overlay.querySelector('.gs-ov-close').addEventListener('click', () => this._closeOverlay());
        overlay.querySelector('.gs-ov-done').addEventListener('click', () => this._closeOverlay());
    }

    _ovRow(type, label, value) {
        return `
            <div class="flex items-center justify-between py-4">
                <label class="text-base font-medium">${label}</label>
                <div class="flex items-center gap-4">
                    <button type="button" class="gs-ov-btn w-10 h-10 rounded-full border-2 border-booking-action text-booking-action text-xl leading-none hover:bg-blue-50 disabled:opacity-30" data-type="${type}" data-delta="-1">−</button>
                    <span class="gs-ov-val w-8 text-center text-base font-medium" data-type="${type}">${value}</span>
                    <button type="button" class="gs-ov-btn w-10 h-10 rounded-full border-2 border-booking-action text-booking-action text-xl leading-none hover:bg-blue-50" data-type="${type}" data-delta="1">+</button>
                </div>
            </div>`;
    }

    _closeOverlay() {
        if (!this._overlay) return;
        this._overlay.remove();
        this._overlay = null;
        document.body.style.overflow = '';
    }

    _updateCount(type, delta) {
        const limits = { adults: { min: 1, max: 30 }, children: { min: 0, max: 10 }, rooms: { min: 1, max: 30 } };
        const limit = limits[type] || { min: 0, max: 99 };

        const hiddenInput = this.querySelector(`.gs-${type}`);
        let value = parseInt(hiddenInput.value) + delta;
        value = Math.max(limit.min, Math.min(limit.max, value));
        hiddenInput.value = value;

        // Update counter display
        const display = this.querySelector(`.gs-val[data-type="${type}"]`);
        if (display) display.textContent = value;

        // Update trigger text
        this._refreshDisplay();
        this._dispatch();
    }

    _refreshDisplay() {
        const adults = parseInt(this.querySelector('.gs-adults').value);
        const children = parseInt(this.querySelector('.gs-children').value);
        const rooms = parseInt(this.querySelector('.gs-rooms').value);
        const display = this.querySelector('.gs-display');
        if (display) display.textContent = this._formatDisplay(adults, children, rooms);
    }

    _formatDisplay(adults, children, rooms) {
        let text = `${adults} adult${adults !== 1 ? 's' : ''}`;
        if (children > 0) text += ` · ${children} child${children !== 1 ? 'ren' : ''}`;
        text += ` · ${rooms} room${rooms !== 1 ? 's' : ''}`;
        return text;
    }

    _dispatch() {
        this.dispatchEvent(new CustomEvent('guests-change', {
            bubbles: true,
            detail: {
                adults: parseInt(this.querySelector('.gs-adults').value),
                children: parseInt(this.querySelector('.gs-children').value),
                rooms: parseInt(this.querySelector('.gs-rooms').value),
            }
        }));
    }
}

customElements.define('guest-selector', GuestSelector);
